/**
 * OnboardingChecklist — first-use setup steps, each decided ONLY by server
 * data (U34, handoff §9 Phase 6). The manual PM path (project → work →
 * teammate) completes it; the agent steps are optional and can be skipped.
 * A failed read shows "Couldn't check", never "not done".
 *
 * Shown on /welcome (after registration) and on the dashboard while a core
 * step is not done. Hiding and skipping are per-user browser conveniences.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { apiClient } from '../../packages/api-client';
import { getProjects, getPortfolio, getUsersPaginated } from '../../packages/api-client/pm-api';
import { listOAuthGrants } from '../../packages/api-client/oauth-grants-api';
import { getAgentCommentCount, getReviews } from '../../packages/api-client/work-api';
import { useAuth } from '../../packages/auth-client';
import { useHasPermission } from '../../packages/permission-guard';
import './OnboardingChecklist.css';

// 'authorized': a connector holds a live grant; it proves approval, not a call.
type StepState = 'loading' | 'done' | 'authorized' | 'todo' | 'error';
type StepId = 'project' | 'task' | 'invite' | 'agent' | 'handoff' | 'review';

interface StepDef {
    id: StepId;
    title: string;
    todo: string;
    to: string;
    action: string;
}

const CORE: StepDef[] = [
    { id: 'project', title: 'Create a project', todo: 'Projects hold your work, milestones and team.', to: '/projects', action: 'Create a project' },
    { id: 'task', title: 'Add work', todo: 'Add a task to a project.', to: '/projects', action: 'Open projects' },
    { id: 'invite', title: 'Invite a teammate', todo: 'Invite someone to this workspace.', to: '/people', action: 'Invite a teammate' },
];

const AGENT: StepDef[] = [
    {
        id: 'agent', title: 'Connect an agent', to: '/connections', action: 'Open Connections',
        todo: 'Done when an API key shows “Last used”. An app you approve shows as authorized: that proves approval, not a call. Copying a snippet does not count.',
    },
    {
        id: 'handoff', title: 'First agent handoff', to: '/connections', action: 'How agents connect',
        todo: 'Done when an agent, signed in with a key or connector, comments on a task you can see.',
    },
    { id: 'review', title: 'Review it', todo: 'Done when a task is in review.', to: '/reviews', action: 'Open Reviews' },
];

/** True if any read says done; throws if none does and one failed (unknown, not "not done"). */
async function anyDone(...reads: Promise<boolean>[]): Promise<boolean> {
    const rs = await Promise.allSettled(reads);
    if (rs.some((r) => r.status === 'fulfilled' && r.value)) return true;
    const failed = rs.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failed) throw failed.reason;
    return false;
}

const CHECKS: Record<StepId, () => Promise<boolean | 'authorized'>> = {
    project: () => getProjects(1, 1).then((r) => (r.data ?? []).length > 0),
    task: () => getPortfolio().then((ps) => ps.some((p) => p.total_tasks > 0)),
    invite: () => anyDone(
        apiClient.get<{ data: unknown[] | null }>('/user-invitations').then((r) => (r.data.data ?? []).length > 0),
        getUsersPaginated(1, 2).then((r) => (r.data ?? []).length >= 2),
    ),
    // Only a used API key is observed use. A listed grant holds an unexpired
    // refresh token (oauth refreshTTL): the user approved an app, which may
    // never have called the server, so it only earns 'authorized'.
    agent: async () => {
        const [used, granted] = await Promise.allSettled([
            apiClient.get<{ data: { last_used_at?: string | null }[] | null }>('/api-keys')
                .then((r) => (r.data.data ?? []).some((k) => !!k.last_used_at)),
            listOAuthGrants().then((g) => (g ?? []).length > 0),
        ]);
        if (used.status === 'fulfilled' && used.value) return true;
        if (granted.status === 'fulfilled' && granted.value) return 'authorized';
        if (used.status === 'rejected') throw used.reason;
        if (granted.status === 'rejected') throw granted.reason;
        return false;
    },
    handoff: () => getAgentCommentCount().then((n) => n > 0),
    review: () => getReviews(1, 1).then((r) => (r.data ?? []).length > 0),
};

// ── Per-user prefs (browser storage is a convenience: it may throw or be empty) ──

interface Prefs { hidden?: boolean; skipAgent?: boolean }
const prefKey = (userId: string) => `ka-onboarding:${userId}`;

function readPrefs(userId: string): Prefs {
    try {
        const raw = localStorage.getItem(prefKey(userId));
        const p: unknown = raw ? JSON.parse(raw) : null;
        return p && typeof p === 'object' ? (p as Prefs) : {};
    } catch {
        return {};
    }
}

function writePrefs(userId: string, p: Prefs) {
    try {
        localStorage.setItem(prefKey(userId), JSON.stringify(p));
    } catch {
        // Kept for this page view only.
    }
}

export function OnboardingChecklist({ variant }: { variant: 'page' | 'dashboard' }) {
    const userId = useAuth().user?.user_id ?? '';
    const canInvite = useHasPermission('users', 'create');
    const [prefs, setPrefs] = useState<Prefs>(() => readPrefs(userId));
    const [states, setStates] = useState<Partial<Record<StepId, StepState>>>({});

    useEffect(() => { setPrefs(readPrefs(userId)); }, [userId]);

    const core = CORE.filter((s) => s.id !== 'invite' || canInvite);
    const ids = [...core, ...AGENT].map((s) => s.id).join(',');

    const load = useCallback(() => {
        let cancelled = false;
        for (const id of ids.split(',') as StepId[]) {
            setStates((prev) => ({ ...prev, [id]: 'loading' }));
            CHECKS[id]()
                .then((done) => { if (!cancelled) setStates((prev) => ({ ...prev, [id]: done === 'authorized' ? 'authorized' : done ? 'done' : 'todo' })); })
                .catch(() => { if (!cancelled) setStates((prev) => ({ ...prev, [id]: 'error' })); });
        }
        return () => { cancelled = true; };
    }, [ids]);
    useEffect(load, [load]);

    function update(p: Prefs) {
        const next = { ...prefs, ...p };
        writePrefs(userId, next); // before setPrefs: a storage failure must not block hiding
        setPrefs(next);
    }

    const stateOf = (id: StepId): StepState => states[id] ?? 'loading';
    const coreDone = core.filter((s) => stateOf(s.id) === 'done').length;
    const coreComplete = coreDone === core.length;
    const anyError = [...core, ...AGENT].some((s) => stateOf(s.id) === 'error');

    if (variant === 'dashboard') {
        // Only while a core step is known not done: never flash while checking,
        // and a failed read (a transient 5xx) on a set-up workspace isn't "not done".
        if (prefs.hidden || core.some((s) => stateOf(s.id) === 'loading') || !core.some((s) => stateOf(s.id) === 'todo')) return null;
    }

    return (
        <section className="card onboarding" aria-labelledby="onboarding-h">
            <div className="onboarding-head">
                <div>
                    <h2 id="onboarding-h" className="onboarding-title">Set up your workspace</h2>
                    <p className="onboarding-muted">
                        {coreComplete
                            ? 'The setup steps are done. The agent steps below are optional.'
                            : `${coreDone} of ${core.length} setup steps done. Everything here works without an agent.`}
                    </p>
                </div>
                {variant === 'page' && prefs.hidden ? (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => update({ hidden: false })}>Show on dashboard</button>
                ) : (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => update({ hidden: true })}>Hide from dashboard</button>
                )}
            </div>

            <ol className="onboarding-steps">
                {core.map((s) => <Step key={s.id} step={s} state={stateOf(s.id)} />)}
            </ol>

            {prefs.skipAgent ? (
                <p className="onboarding-muted">
                    Agent steps skipped.{' '}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => update({ skipAgent: false })}>Show agent steps</button>
                </p>
            ) : (
                <div className="onboarding-optional">
                    <div className="onboarding-head">
                        <h3 className="onboarding-subtitle">Optional: hand work to an agent</h3>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => update({ skipAgent: true })}>Skip agent steps</button>
                    </div>
                    <ol className="onboarding-steps">
                        {AGENT.map((s) => <Step key={s.id} step={s} state={stateOf(s.id)} />)}
                    </ol>
                </div>
            )}

            {anyError && (
                <p role="alert" className="onboarding-muted">
                    Some steps couldn't be checked: the server didn't answer.{' '}
                    <button type="button" className="btn btn-secondary btn-sm" onClick={load}>Check again</button>
                </p>
            )}
        </section>
    );
}

const STATUS: Record<StepState, string> = {
    loading: 'Checking…', done: 'Done', authorized: 'Authorized', todo: 'Not done yet', error: "Couldn't check",
};

function Step({ step, state }: { step: StepDef; state: StepState }) {
    const Icon = state === 'done' ? CheckCircle2 : state === 'error' ? AlertCircle : Circle;
    return (
        <li className={`onboarding-step onboarding-step--${state}`} aria-label={step.title}>
            <Icon size={18} aria-hidden="true" className="onboarding-icon" />
            <div className="onboarding-step-body">
                <div className="onboarding-step-title">
                    {step.title} <span className="onboarding-status">{STATUS[state]}</span>
                </div>
                {state !== 'done' && state !== 'loading' && (
                    <p className="onboarding-muted">
                        {state === 'error' ? "We couldn't read this from the server, so we don't know yet."
                            : state === 'authorized' ? 'Authorized — an app you approved holds a live token.'
                            : step.todo}{' '}
                        <Link to={step.to}>{step.action}</Link>
                    </p>
                )}
            </div>
        </li>
    );
}
